import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useContent, useContentMutations } from "@/hooks";
import {
	describeSource,
	describeStatus,
	formatBytes,
	isBusy,
	quotaState,
} from "@/lib/content";
import { ApiError } from "@/services";
import type { OrgContentSource } from "@/types/api";
import { FileText, Link2, Loader2, Type, Upload } from "lucide-react";
import { useRef, useState } from "react";

type Mode = "file" | "url" | "text";

/**
 * The partner's own material — the screen that makes Curo feel like theirs.
 */
export function ContentPage() {
	const { data, isLoading } = useContent();
	const { addUrl, addText, addFile, retry, remove } = useContentMutations();

	const [mode, setMode] = useState<Mode>("file");
	const [url, setUrl] = useState("");
	const [title, setTitle] = useState("");
	const [text, setText] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [dragging, setDragging] = useState(false);
	const [pendingRemoval, setPendingRemoval] = useState<OrgContentSource | null>(
		null,
	);
	const fileInput = useRef<HTMLInputElement>(null);

	const sources = data?.sources ?? [];
	const usage = data?.usage;
	const q = usage ? quotaState(usage.used, usage.quota) : null;
	const busy = addUrl.isPending || addText.isPending || addFile.isPending;

	async function run(fn: () => Promise<unknown>, onOk?: () => void) {
		setError(null);
		setNotice(null);
		try {
			const result = (await fn()) as { duplicate?: boolean } | undefined;
			// A duplicate is not an error and not a success — nothing was added,
			// and saying so beats a silent no-op that looks like a failed upload.
			if (result?.duplicate) {
				setNotice("That document is already in your library.");
			}
			onOk?.();
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Could not add that. Try again.",
			);
		}
	}

	const onFiles = (files: FileList | null) => {
		const file = files?.[0];
		if (file) run(() => addFile.mutateAsync(file));
	};

	return (
		<>
			<PageHeader
				title="Content"
				description="Your own material, taught to your team alongside everything else."
			/>

			<Card className="mb-6">
				<CardContent className="p-5">
					<div className="mb-4 flex gap-1.5">
						{(
							[
								["file", "Upload a file", Upload],
								["url", "From a link", Link2],
								["text", "Paste text", Type],
							] as const
						).map(([m, label, Icon]) => (
							<button
								key={m}
								type="button"
								onClick={() => {
									setMode(m);
									setError(null);
									setNotice(null);
								}}
								className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium transition-colors ${
									mode === m
										? "bg-surface3 text-heading"
										: "text-muted-foreground hover:text-heading"
								}`}
							>
								<Icon className="size-3.5" />
								{label}
							</button>
						))}
					</div>

					{mode === "file" ? (
						/* biome-ignore lint/a11y/useKeyWithClickEvents: the button below is the keyboard path */
						<div
							onDragOver={(e) => {
								e.preventDefault();
								setDragging(true);
							}}
							onDragLeave={() => setDragging(false)}
							onDrop={(e) => {
								e.preventDefault();
								setDragging(false);
								onFiles(e.dataTransfer.files);
							}}
							className={`flex flex-col items-center gap-2 rounded-md border border-dashed px-6 py-8 text-center transition-colors ${
								dragging
									? "border-orange-stroke bg-orange-surface"
									: "border-divider bg-surface2"
							}`}
						>
							<Upload className="size-5 text-muted-foreground" />
							<p className="text-body text-heading">Drop a document here, or</p>
							<Button
								variant="secondary"
								size="sm"
								disabled={busy}
								onClick={() => fileInput.current?.click()}
							>
								{addFile.isPending ? "Uploading…" : "Choose a file"}
							</Button>
							<input
								ref={fileInput}
								type="file"
								className="hidden"
								onChange={(e) => onFiles(e.target.files)}
							/>
							{/* The limits, before they hit them rather than after. */}
							<p className="text-caption text-muted-foreground">
								PDF, Word, PowerPoint, text or Markdown — up to 25MB
							</p>
						</div>
					) : mode === "url" ? (
						<div className="flex flex-col gap-2 sm:flex-row">
							<Input
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								placeholder="https://example.com/guide"
							/>
							<Input
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="Title (optional)"
								className="sm:max-w-[220px]"
							/>
							<Button
								disabled={!url.trim() || busy}
								onClick={() =>
									run(
										() =>
											addUrl.mutateAsync({
												url: url.trim(),
												title: title.trim() || undefined,
											}),
										() => {
											setUrl("");
											setTitle("");
										},
									)
								}
							>
								{addUrl.isPending ? "Adding…" : "Add"}
							</Button>
						</div>
					) : (
						<div className="flex flex-col gap-2">
							<Input
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="Title (optional)"
							/>
							<textarea
								value={text}
								onChange={(e) => setText(e.target.value)}
								rows={6}
								placeholder="Paste the text you want your team taught from…"
								className="w-full rounded-md border border-divider bg-surface-primary px-3 py-2 text-body text-heading placeholder:text-muted1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							/>
							<div>
								<Button
									disabled={!text.trim() || busy}
									onClick={() =>
										run(
											() =>
												addText.mutateAsync({
													text: text.trim(),
													title: title.trim() || undefined,
												}),
											() => {
												setText("");
												setTitle("");
											},
										)
									}
								>
									{addText.isPending ? "Adding…" : "Add"}
								</Button>
							</div>
						</div>
					)}

					{error ? (
						<p className="mt-3 text-caption text-error">{error}</p>
					) : null}
					{notice ? (
						<p className="mt-3 text-caption text-muted-foreground">{notice}</p>
					) : null}
				</CardContent>
			</Card>

			{/*
			 * Quota is stated only when it is close to mattering. A bar that is
			 * always shouting gets ignored, and what an admin needs to know is
			 * whether the next document will fit — not a running percentage.
			 */}
			{q && q.tone !== "ok" && usage ? (
				<div
					className={`mb-6 rounded-md border px-3 py-2.5 text-caption ${
						q.tone === "full"
							? "border-error-stroke bg-error-surface text-error"
							: "border-orange-stroke bg-orange-surface text-orange-text"
					}`}
				>
					{q.tone === "full"
						? `Your content library is full (${usage.used.toLocaleString()} of ${usage.quota.toLocaleString()} indexed sections). Remove a document, or contact your Curo account manager to raise the limit.`
						: `Your content library is ${q.pct}% full (${usage.used.toLocaleString()} of ${usage.quota.toLocaleString()} indexed sections).`}
				</div>
			) : null}

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : sources.length === 0 ? (
				<EmptyState
					title="No content yet"
					description="Add your own guides, policies or training material above. Your team will be taught from it alongside everything else in Curo."
				/>
			) : (
				<Card>
					<div className="overflow-x-auto">
						<table className="w-full text-body">
							<thead>
								<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
									<Th>Document</Th>
									<Th>Status</Th>
									<Th className="text-right">Sections</Th>
									<Th>Added</Th>
									<Th />
								</tr>
							</thead>
							<tbody>
								{sources.map((s) => (
									<Row
										key={s.id}
										source={s}
										onRetry={() => run(() => retry.mutateAsync(s.id))}
										onRemove={() => setPendingRemoval(s)}
										retrying={retry.isPending}
									/>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			<AlertDialog
				open={pendingRemoval !== null}
				onOpenChange={(o) => !o && setPendingRemoval(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Remove “{pendingRemoval?.title}”?
						</AlertDialogTitle>
						{/* Says the real consequence: this stops being taught. */}
						<AlertDialogDescription>
							Your team will stop being taught from this document, and it will
							no longer appear in answers.{" "}
							<span className="text-heading">
								The file and everything indexed from it are deleted
							</span>{" "}
							— you can add it again at any time.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep it</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								if (pendingRemoval)
									run(() => remove.mutateAsync(pendingRemoval.id));
								setPendingRemoval(null);
							}}
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function Row({
	source,
	onRetry,
	onRemove,
	retrying,
}: {
	source: OrgContentSource;
	onRetry: () => void;
	onRemove: () => void;
	retrying: boolean;
}) {
	const s = describeStatus(source.status);
	const size = formatBytes(source.byte_size);
	const working = isBusy(source.status);

	return (
		<tr className="border-b border-divider last:border-0">
			<Td>
				<div className="flex items-start gap-2">
					<FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
					<div className="min-w-0">
						<div className="truncate font-medium text-heading">
							{source.title}
						</div>
						<div className="truncate text-caption text-muted-foreground">
							{describeSource(source)}
							{size ? ` · ${size}` : ""}
						</div>
						{/*
						 * A failure states the reason on the row. "Failed" alone
						 * leaves an admin with a broken document and no idea
						 * whether to fix the file or try again.
						 */}
						{source.status === "failed" && source.error ? (
							<div className="mt-0.5 text-caption text-error">
								{source.error}
							</div>
						) : null}
					</div>
				</div>
			</Td>
			<Td>
				<div className="flex items-center gap-1.5">
					{working ? (
						<Loader2 className="size-3.5 animate-spin text-orange-text" />
					) : null}
					<Badge variant={s.tone}>{s.label}</Badge>
				</div>
				{s.hint ? (
					<div className="mt-0.5 text-caption text-muted-foreground">
						{s.hint}
					</div>
				) : null}
			</Td>
			<Td className="tabular text-right text-muted-foreground">
				{source.status === "ready" ? source.chunk_count.toLocaleString() : "—"}
			</Td>
			<Td className="tabular text-muted-foreground">
				<div>{new Date(source.created_at).toLocaleDateString()}</div>
				{source.uploader_email ? (
					<div className="truncate text-caption">
						{source.uploader_name ?? source.uploader_email}
					</div>
				) : null}
			</Td>
			<Td className="text-right">
				<div className="flex justify-end gap-1">
					{source.status === "failed" ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={onRetry}
							disabled={retrying}
						>
							Retry
						</Button>
					) : null}
					<Button variant="ghost" size="sm" onClick={onRemove}>
						Remove
					</Button>
				</div>
			</Td>
		</tr>
	);
}

function Th({
	children,
	className,
}: { children?: React.ReactNode; className?: string }) {
	return (
		<th className={`px-4 py-2.5 text-left font-medium ${className ?? ""}`}>
			{children}
		</th>
	);
}

function Td({
	children,
	className,
}: { children?: React.ReactNode; className?: string }) {
	return (
		<td className={`px-4 py-3 align-top ${className ?? ""}`}>{children}</td>
	);
}
